import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Search, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { ALL_ISRAELI_CITIES, SHIPPING_PRICES } from '@/lib/shippingConfig';

export default function CitiesManagementModule() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [centralCities, setCentralCities] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [centralPrice, setCentralPrice] = useState<number>(SHIPPING_PRICES.delivery.central);
  const [remotePrice, setRemotePrice] = useState<number>(SHIPPING_PRICES.delivery.remote);

  useEffect(() => {
    loadCitiesConfig();
  }, []);

  const loadCitiesConfig = async () => {
    try {
      const { data: settingsData, error: settingsError } = await (supabase as any)
        .from('site_settings')
        .select('key, value')
        .in('key', ['central_zone_cities', 'shipping_price_central', 'shipping_price_remote']);

      if (settingsError) throw settingsError;

      if (settingsData) {
        const citiesVal = settingsData.find(d => d.key === 'central_zone_cities')?.value;
        const centralVal = settingsData.find(d => d.key === 'shipping_price_central')?.value;
        const remoteVal = settingsData.find(d => d.key === 'shipping_price_remote')?.value;

        if (citiesVal) {
          setCentralCities(JSON.parse(citiesVal));
        } else {
          const { CENTRAL_ZONE_CITIES } = await import('@/lib/shippingConfig');
          setCentralCities(CENTRAL_ZONE_CITIES);
        }

        if (centralVal) setCentralPrice(parseInt(centralVal));
        if (remoteVal) setRemotePrice(parseInt(remoteVal));
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('שגיאה בטעינת הגדרות');
    } finally {
      setLoading(false);
    }
  };

  const saveCitiesConfig = async () => {
    setSaving(true);
    try {
      const settingsToUpsert = [
        { key: 'central_zone_cities', value: JSON.stringify(centralCities) },
        { key: 'shipping_price_central', value: centralPrice.toString() },
        { key: 'shipping_price_remote', value: remotePrice.toString() }
      ];

      const { error } = await (supabase as any)
        .from('site_settings')
        .upsert(settingsToUpsert, { onConflict: 'key' });

      if (error) throw error;

      toast.success('ההגדרות נשמרו בהצלחה');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('שגיאה בשמירת הגדרות: ' + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const moveToCentral = (city: string) => {
    if (!centralCities.includes(city)) {
      setCentralCities([...centralCities, city]);
    }
  };

  const moveToRemote = (city: string) => {
    setCentralCities(centralCities.filter(c => c !== city));
  };

  const filteredCentralCities = centralCities.filter(city =>
    city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRemoteCities = ALL_ISRAELI_CITIES.filter(city =>
    !centralCities.includes(city) &&
    city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">ניהול אזורי משלוח</h2>
          <p className="text-muted-foreground">נהל את הערים ואת מחירי המשלוח לכל אזור</p>
        </div>
        <Button onClick={saveCitiesConfig} disabled={saving}>
          {saving && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          שמור שינויים
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>מחירי משלוח</CardTitle>
          <CardDescription>קבע את עלות המשלוח לכל אזור (כולל מע"מ)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">מחיר אזור מרכז (₪)</label>
              <Input
                type="number"
                value={centralPrice}
                onChange={(e) => setCentralPrice(parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">מחיר אזור מרוחק (₪)</label>
              <Input
                type="number"
                value={remotePrice}
                onChange={(e) => setRemotePrice(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="absolute right-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="חפש עיר לניהול שיוך אזור..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pr-10"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Central Zone */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🟢 אזור מרכז (₪{centralPrice})
              <Badge variant="secondary">{filteredCentralCities.length} ערים</Badge>
            </CardTitle>
            <CardDescription>חדרה עד גדרה</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredCentralCities.map((city) => (
                <div
                  key={city}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <span className="font-medium">{city}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveToRemote(city)}
                    title="העבר לאזור מרוחק"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {filteredCentralCities.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  {searchTerm ? 'לא נמצאו ערים' : 'אין ערים באזור זה'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Remote Zone */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🟡 אזור מרוחק (₪{remotePrice})
              <Badge variant="secondary">{filteredRemoteCities.length} ערים</Badge>
            </CardTitle>
            <CardDescription>צפון ודרום הארץ</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredRemoteCities.map((city) => (
                <div
                  key={city}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <span className="font-medium">{city}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveToCentral(city)}
                    title="העבר לאזור מרכז"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {filteredRemoteCities.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  {searchTerm ? 'לא נמצאו ערים' : 'אין ערים באזור זה'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
